<template>
  <div class="payment wrapper">
    <content-title title="주문" :steps="steps" :activeStep="2" />
    <payment-orderer-info ref="payment-orderer-info" class="payment-order-info" @input="getPhone" />
    <payment-recipient-info ref="payment-recipient-info" class="payment-recipient-info" :ordererPhone="ordererPhone" />
    <payment-product-info-table class="payment-product-info-table" />
    <payment-receive-email ref="payment-receive-email" class="payment-receive-email" />
    <payment-info ref="payment-info" class="payment-info" />
    <payment-privacy-policy
      ref="payment-privacy-policy"
      checkboxLabel="위 안내사항 및 주문 내용을 확인했으며, 주문을 진행합니다."
      class="payment-privacy-policy"
    />
    <div class="button-wrapper">
      <button class="btn lg btn-primary" @click="onClickPay">주문하기</button>
    </div>
    <payment-inicis ref="payment-inicis" />

    <modal-payment-loading />
    <modal-password-confirm id="ModalPasswordConfirm2" :isPay="true" @regist="onSuccessPassword" />
  </div>
</template>

<script>
import PaymentOrdererInfo from '@/views/payment/components/PaymentOrdererInfo.vue'
import PaymentRecipientInfo from '@/views/payment/components/PaymentRecipientInfo.vue'
import PaymentProductInfoTable from '@/views/payment/components/PaymentProductInfoTable.vue'
import PaymentReceiveEmail from '@/views/payment/components/PaymentReceiveEmail.vue'
import PaymentInfo from '@/views/payment/components/PaymentInfo.vue'
import PaymentPrivacyPolicy from '@/views/payment/components/PaymentPrivacyPolicy.vue'
import PaymentInicis from '@/views/payment/components/PaymentInicis.vue'
import ModalPaymentLoading from '@/components/modal/ModalPaymentLoading.vue'
import ContentTitle from '@/components/content/ContentTitle'
import ModalPasswordConfirm from '@/components/modal/ModalPasswordConfirm'
import PageName from '@/utils/define/PageName'
import { mapState, mapGetters, mapActions } from 'vuex'
import { ToastType } from '@/utils/define/ToastType'
import cookies from 'vue-cookies'
import { requestGaEventPurchase } from '@/utils/ga'

const PAYMENT_METHOD = {
  DIRECT_CARD: 'DIRECT_CARD',
  LATER: 'LATER',
}

const INSTITUTE_TYPE = {
  PERSON: 'PERSON',
  UNIVERSITY: 'UNIVERSITY',
  ENTERPRISE: 'ENTERPRISE',
  SELLER: 'SELLER',
}

export default {
  name: 'Payment',
  components: {
    PaymentOrdererInfo,
    PaymentRecipientInfo,
    PaymentProductInfoTable,
    PaymentReceiveEmail,
    PaymentInfo,
    PaymentPrivacyPolicy,
    ContentTitle,
    ModalPasswordConfirm,
    PaymentInicis,
    ModalPaymentLoading,
  },
  data() {
    return {
      steps: [
        { value: 1, text: '장바구니' },
        { value: 2, text: '주문' },
        { value: 3, text: '주문 완료' },
      ],
      ordererPhone: '',
      _inicisHandler: null, // message 이벤트 핸들러 레퍼런스
    }
  },
  computed: {
    ...mapState('delivery', ['basicAddress']),
    ...mapState('order', ['estimateId', 'selectedProductList']),
    ...mapState('user', ['user', 'institute']),
    ...mapGetters('user', ['instituteId']),
    isPerson() {
      if (this.$_.isEmpty(this.institute)) return false
      return this.institute.type.code === INSTITUTE_TYPE.PERSON
    },
    currentAddress() {
      return cookies.get('currentAddress2') ?? this.basicAddress
    },
  },
  methods: {
    ...mapActions('order', ['order']),
    onClickPay() {
      if (!this.validate()) return
      const paymentInfo = this.$refs['payment-info'].getPaymentInfo()
      // eslint-disable-next-line no-console
      console.log('[Payment] paymentInfo:', paymentInfo)

      // 카드 간편결제는 제거됨. DIRECT_CARD(일반) 또는 LATER(후불)만 존재
      if (paymentInfo.paymentMethod === PAYMENT_METHOD.DIRECT_CARD) {
        // 일반결제는 결제 비밀번호 모달 없이 바로 진행 (필요시 모달 유지 가능)
        this.pay(this.makePayload())
      } else {
        // 후불결제
        this.pay(this.makePayload())
      }
    },

    async pay(payload) {
      try {
        this.$bvModal.show('ModalPaymentLoading')

        const response = await this.order({ instituteId: this.instituteId, body: payload })

        const paymentInfo = this.$refs['payment-info'].getPaymentInfo()
        if (paymentInfo.isRememberPaymentMethod === true) cookies.set('paymentMethod', paymentInfo.paymentMethod)

        if (paymentInfo.paymentMethod === PAYMENT_METHOD.DIRECT_CARD) {
          // 일반결제: 이니시스 결제창 호출
          this.addPostMSGEvent()

          // 서버 응답에 총액/주문명을 내려주면 함께 전달(안전)
          const amount = response?.totalPrice
          const goodsName = response?.orderName

          this.$refs['payment-inicis'].pay({
            orderId: response.orderId,               // oid
            payerName: payload.ordererName,          // buyername
            payerPhone: payload.ordererPhone,        // buyertel (숫자만 권장)
            email: payload.ordererEmail,             // buyeremail
            instituteType: this.institute?.type?.code, // 로깅/디버깅용
            ...(amount ? { amount } : {}),
            ...(goodsName ? { goodsName } : {}),
          })
        } else {
          // 후불결제: PG 호출 없이 완료 처리
          await requestGaEventPurchase(this.instituteId, response.orderId)
          this.goPaymentComplete(response.orderId)
        }

        this.$root.darkToast('주문이 정상적으로 접수되었습니다.')
      } catch (e) {
        const msg = e?.response?.data?.msg || e?.message || '주문 처리 중 오류가 발생했습니다.'
        this.$root.toast('에러', msg, ToastType.ERROR)
      } finally {
        this.$bvModal.hide('ModalPaymentLoading')
      }
    },

    addPostMSGEvent() {
      const self = this

      // 중복 등록 방지
      if (self._inicisHandler) {
        window.removeEventListener('message', self._inicisHandler)
      }

      self._inicisHandler = async function (e) {
        const { type, data } = e.data || {}
        if (type === 'inicisCallback') {
          const { result, orderId, errorMessage } = data || {}
          // 디버깅 로그
          // eslint-disable-next-line no-console
          console.debug('[INICIS CALLBACK]', data)

          if (result == 'true' || result == true) {
            await requestGaEventPurchase(self.instituteId, orderId)
            self.goPaymentComplete(orderId)
          } else {
            self.$root?.toast?.('에러', errorMessage || '결제에 실패했습니다.', ToastType.ERROR)
          }
        }
      }
      window.addEventListener('message', self._inicisHandler)
    },

    goPaymentComplete(orderId) {
      this.$router.replace({
        name: PageName.PaymentComplete,
        params: { id: orderId },
      })
    },

    onSuccessPassword(password) {
      // (카드 간편결제 제거됨 — 필요하면 재활성)
      const paymentInfo = this.$refs['payment-info'].getPaymentInfo()
      const payload = this.makePayload()
      payload.cardId = paymentInfo.cardId
      payload.cardPaymentPassword = password
      this.pay(payload)
    },

    makePayload() {
      const recipient = this.$refs['payment-recipient-info'].getRecipient()
      const ordererPhone = this.$refs['payment-orderer-info'].getPhone()
      const paymentInfo = this.$refs['payment-info'].getPaymentInfo()
      const products = this.selectedProductList.map(({ productId, quantity, cartId }) => {
        return { id: productId, quantity: quantity, cartId: cartId }
      })

      const payload = {
        ordererName: this.user.name,
        ordererPhone: ordererPhone.replace(/-/gi, ''),
        ordererEmail: this.user.email,
        recipientName: recipient.name,
        recipientPhone: recipient.phone.replace(/-/gi, ''),
        deliveryRequest: recipient.requestedTerm,
        deliveryId: this.currentAddress.id,
        products: products,
        paymentMethod: paymentInfo.paymentMethod, // DIRECT_CARD 또는 LATER
        requestId: this.$route.query?.requestId ?? null,
      }
      if (this.estimateId) payload.estimateId = this.estimateId

      if (!this.isPerson) {
        const email = this.$refs['payment-receive-email'].getEmail()
        payload.documentReceiveEmail = email
      }

      return payload
    },

    validate() {
      let isValid = true
      const paymentInfo = this.$refs['payment-info'].getPaymentInfo()
      // eslint-disable-next-line no-console
      console.log('[Payment validate] paymentInfo:', paymentInfo)

      if (!paymentInfo.paymentMethod || paymentInfo.paymentMethod !== PAYMENT_METHOD.LATER) {
        alert('후불결제를 선택해주세요.')
        isValid = false
      } else if (!this.$refs['payment-orderer-info'].validate()) {
        isValid = false
      } else if (!this.$refs['payment-recipient-info'].validate()) {
        isValid = false
      } else if (!this.$refs['payment-privacy-policy'].validate()) {
        isValid = false
      }

      return isValid
    },

    getPhone(val) {
      this.ordererPhone = val
    },
  },
  beforeDestroy() {
    // 이벤트 리스너 정리
    if (this._inicisHandler) {
      window.removeEventListener('message', this._inicisHandler)
      this._inicisHandler = null
    }
  },
}
</script>

<style lang="scss" scoped>
.payment {
  .payment-order-info { margin-top: 30px; }
  .payment-recipient-info { margin-top: 40px; }
  .payment-product-info-table { margin-top: 20px; }
  .payment-receive-email { margin-top: 40px; }
  .payment-info { margin-top: 30px; }
  .payment-privacy-policy { margin-top: 30px; }

  .button-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 40px;
    button {
      width: 400px;
      height: 48px;
      font-size: 1rem;
      &.btn-primary { background-color: $PRIMARY_YELLOW; }
    }
  }
}
</style>
